import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { GridHeader, GridRow } from 'src/components/Reusable/Grid'
import { StickyGridHeader } from 'src/components/Reusable/Grid/Grid.styles'
import {
  DOCUMENT_TYPE,
  EVIDENCE_LINKAGE_TYPE,
  EvidenceWrapper,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { formatISODateTime } from 'src/Utils/formatDate'
import { useFileTypeIconForEvidence } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { Gravatar } from 'src/components/Gravatar'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  RowItemElement,
  RowItemExpandableElement,
  RowItemProp,
} from 'src/components/Reusable/Grid/Accordion/RowItemProp'
import { IconButton, IconButtonVariant } from 'src/components/Reusable/Buttons'
import {
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { EvidenceGridColumnSort } from 'src/components/Reusable/Grid/GridColumnSort/EvidenceGridColumnSort'
import { gridColSortReducerEvidence } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.utils'
import {
  GRID_COL_SORT_CONFIGS_EVIDENCE,
  GridColumnSortActionEvidence,
  GridColumnSortEvidence,
  GridColumnSortType,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { FlexAlign } from 'src/components/Reusable/Flex'
import { useUrlSearchParams } from 'src/Utils/helpers/navigation'
import { useTypewriter } from 'src/Utils/globalHooks'
import { formatEvidenceCaption } from 'src/Utils/helpers/string.helpers'
import { useDeleteEvidenceGroup } from 'src/components/ModalForms/Evidence/useDeleteEvidence'
import {
  bannerHeight,
  filterBarHeight,
  headerContentHeight,
} from 'src/components/PageLayout/dimensionUtils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import { useBannerContext } from 'src/context/Banner/BannerContext'
import { useScanReceptor } from 'src/pages/Receptors/receptors.hooks'
import { BrokenLinkIcon } from 'src/components/Icons/Basic/BrokenLinkIcon'
import { DocToScan } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { useReceptorScanContext } from 'src/context/ReceptorScanStatus'
import { EvidenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { isTrArtifactMime } from 'src/components/ModalForms/Evidence/evidenceUtils'
import { ArtifactEvidenceSourceLink } from 'src/components/Reusable/Evidence/ArtifactEvidenceSourceLink'
import { getEvidenceSource } from 'src/Utils/Evidence/evidence.helpers'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useIsContentSearchActive } from 'src/Utils/search/search.hooks'
import { ContentMatchBadge } from 'src/components/Reusable/Search/ContentSearch/ContentSearch.components'
import { useSearchContext } from 'src/Utils/search/search.context'
import { Spinner } from 'src/Throbber'
import { ReceptorLogo } from 'src/components/async/model/receptor/ReceptorLogo'
import { useInAudit } from 'src/context/AuditContext'
import {
  EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS,
  EVIDENCE_INDEX_HEADER_TO_HEADER_VALUE,
  EvidenceIndexHeader,
} from '../evidence.constants'
import {
  useEvidenceIndexHeaders,
  useEvidenceInstanceList,
  useEvidencePermalinkForGrid,
} from '../evidence.hooks'
import {
  EvidenceDeleteButton,
  EvidenceRelevantDateButton,
  EvidenceRowMenu,
} from '../evidence.components'
import { buildEvidenceShowLinkWithSource } from '../evidence.helpers'
import {
  StyledEvidenceColumn,
  StyledActionEvidenceColumn,
} from './EvidenceGrid.styles'

export const EvidenceWrapperGridRow = ({
  evidenceWrapper,
  gridRow,
  total,
  isLinkBroken,
  showControlCount,
  requestId,
  linkedRequestsCount,
  onUpdate,
  isControlShowPage,
}: {
  evidenceWrapper: EvidenceWrapper
  gridRow?: number
  total?: number
  isLinkBroken?: boolean
  showControlCount?: boolean
  requestId?: string
  linkedRequestsCount?: number
  onUpdate: () => Promise<void>
  isControlShowPage?: boolean
}): JSX.Element => {
  const canDeleteEvidence = useCanPerformAction(CAPABILITY.CAN_DELETE_EVIDENCE)
  const { auditId } = useInAudit()
  const location = useLocation()
  const isContentSearchActive = useIsContentSearchActive()
  const { contentMatchIds } = useSearchContext()
  const newModel = useNewModel()
  const navigate = useNavigate()
  const hasSharepoint = IsGatekeeperEnabled(GatekeeperFlag.SHAREPOINT)
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const { scanInProgressState } = useReceptorScanContext()
  const scanNow = useScanReceptor()
  const caption = evidenceWrapper.getCaption()
  const contentId = evidenceWrapper.getContentId()
  const discoveryId = evidenceWrapper.getDiscoveryId()
  const relevantDate = evidenceWrapper.getRelevantDate()
  const lastModified = evidenceWrapper.getDocModifiedAt()
  const lastScanned = useMemo(
    () => evidenceWrapper.getDocScannedAt(),
    [evidenceWrapper],
  )
  const createdAt = evidenceWrapper.getCreatedAt()
  const actorId = evidenceWrapper.getActorId()
  const actor = evidenceWrapper.getActorEmail()
  const importKey = evidenceWrapper.getDocImportKey()

  const noEvidenceInstanceId =
    newModel && !evidenceWrapper.getEvidenceInstanceId()

  const FileTypeIcon = useFileTypeIconForEvidence(
    evidenceWrapper.getIsAutomated(),
    evidenceWrapper.getMime(),
    evidenceWrapper.getIsLinkedDocument(),
    evidenceWrapper.getServiceModelId(),
  )
  const deleteFunc = useDeleteEvidenceGroup({
    caption,
    contentId,
    discoveryId,
    total,
    evidenceId: evidenceWrapper.getEvidenceId(),
    evidenceInstanceId: evidenceWrapper.getEvidenceInstanceId(),
    evidenceLinkedControlsCount: evidenceWrapper.getLinkedControlsCount(),
    onUpdate,
  })

  const editableRelevantDate =
    !evidenceWrapper.getIsAutomated() || evidenceWrapper.getIsLinkedDocument()

  const evidencePermalink = useEvidencePermalinkForGrid(evidenceWrapper.getId())

  const evidenceLink = buildEvidenceShowLinkWithSource(
    evidencePermalink,
    location,
  )
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (noEvidenceInstanceId) {
      return
    }
    e.stopPropagation()
    e.preventDefault()
    sessionStorage.setItem(
      `scrollPosition-${location.pathname}`,
      window.scrollY.toString(),
    )
    navigate(evidenceLink)
  }

  const isAutomated = evidenceWrapper.getIsAutomated()
  const isConfluence = evidenceWrapper.getActorEmail() === 'trr-confluence-dms'
  const isSharepoint = evidenceWrapper.getActorEmail() === 'trr-sharepoint'
  const isGoogleDrive = evidenceWrapper.getActorEmail() === 'trr-gdrive'
  const isViaRequest = evidenceWrapper.getIsViaRequest()
  const isTruncated = caption.length > 50
  const formattedCaption = formatEvidenceCaption(
    caption,
    isAutomated,
    isTruncated,
    isViaRequest,
    isControlShowPage,
  )

  useEffect(() => {
    const scanning = scanInProgressState[`${actorId}-${importKey}`]

    setIsScanning((prev) => {
      if (prev && prev !== scanning) {
        onUpdate()
        return false
      } else if (!prev && scanning) {
        return true
      }
      return prev
    })
  }, [onUpdate, scanInProgressState, actorId, importKey])

  const isLinkedDocument = evidenceWrapper.getIsLinkedDocument()
  const docImportLink = evidenceWrapper.getDocImportLink()
  const mime = evidenceWrapper.getMime()
  const source =
    isTrArtifactMime(mime) && contentId ? (
      <ArtifactEvidenceSourceLink
        contentId={contentId}
        mime={mime}
        style={{ textAlign: 'left' }}
      />
    ) : (
      getEvidenceSource(
        isAutomated,
        isLinkedDocument,
        actor,
        docImportLink,
        mime,
      )
    )

  const isContentMatch = useMemo(
    () => contentMatchIds.has(evidenceWrapper.getId() || ''),
    [contentMatchIds, evidenceWrapper],
  )
  const isEvidenceIndexPage = location.pathname.includes(
    EvidenceAbsoluteRoutes.INDEX,
  )
  const showContentMatchColumn = isContentSearchActive && isEvidenceIndexPage

  return (
    <>
      {showContentMatchColumn && (
        <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
          <ContentMatchBadge isContentMatch={isContentMatch} />
        </StyledEvidenceColumn>
      )}
      <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
        {isLinkBroken ? (
          <BrokenLinkIcon />
        ) : (
          <FileTypeIcon width="24px" height="24px" />
        )}
      </StyledEvidenceColumn>
      <Tooltip
        id={`${evidenceWrapper.getId()}-caption`}
        tooltipBody={caption}
        placement={TooltipPositions.top}
        overlayType={TooltipOverlayType.popover}
      >
        <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
          {formattedCaption}
        </StyledEvidenceColumn>
      </Tooltip>
      <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
        {source}
      </StyledEvidenceColumn>
      {auditId && (
        <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
          {linkedRequestsCount || 0}
        </StyledEvidenceColumn>
      )}
      {showControlCount && (
        <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
          {evidenceWrapper.getLinkedControlsCount() || 0}
        </StyledEvidenceColumn>
      )}
      <Tooltip
        id={`${evidenceWrapper.getId()}-${evidenceWrapper.getActorName()}`}
        placement="top-end"
        tooltipBody={evidenceWrapper.getActorName()}
        disabled={!evidenceWrapper.getActorName()}
      >
        <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
          {isAutomated && actor.startsWith('trr-') ? (
            <ReceptorLogo receptorModelId={actor} width="24px" height="24px" />
          ) : (
            <Gravatar
              email={evidenceWrapper.getActorEmail()}
              emptyIsUnassigned={false}
            />
          )}
        </StyledEvidenceColumn>
      </Tooltip>
      <StyledEvidenceColumn
        gridRow={gridRow}
        onClick={editableRelevantDate && relevantDate ? undefined : onClick}
      >
        {editableRelevantDate && relevantDate ? (
          <EvidenceRelevantDateButton
            evidenceInstanceId={evidenceWrapper.getEvidenceInstanceId()}
            relevantDate={relevantDate}
            onUpdateRelevantDate={() => onUpdate()}
          />
        ) : (
          <>{relevantDate && formatISODateTime(relevantDate)}</>
        )}
      </StyledEvidenceColumn>
      {hasSharepoint ? (
        <>
          <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
            {lastModified && formatISODateTime(lastModified)}
          </StyledEvidenceColumn>
          <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
            {lastScanned && formatISODateTime(lastScanned)}
          </StyledEvidenceColumn>
          <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
            {(isSharepoint || isGoogleDrive || isConfluence) && (
              <>
                {isScanning ? (
                  <Spinner size="s" color="secondary" />
                ) : (
                  <IconButton
                    variant={IconButtonVariant.icon}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      scanNow({
                        receptorModelId: actor,
                        receptorId: actorId,
                        docsToScan: [
                          new DocToScan()
                            .setDocId(importKey)
                            .setDocType(DOCUMENT_TYPE.EVIDENCE),
                        ],
                      })
                    }}
                  >
                    <FontAwesomeIcon icon={faRefresh} />
                  </IconButton>
                )}
              </>
            )}
          </StyledEvidenceColumn>
        </>
      ) : (
        <StyledEvidenceColumn gridRow={gridRow} onClick={onClick}>
          {createdAt && formatISODateTime(createdAt)}
        </StyledEvidenceColumn>
      )}
      <StyledActionEvidenceColumn gridRow={gridRow} onClick={onClick}>
        {!isViaRequest && (
          <>
            {requestId || isControlShowPage ? (
              <EvidenceRowMenu
                evidenceId={evidenceWrapper.getEvidenceId()}
                evidenceInstanceId={evidenceWrapper.getEvidenceInstanceId()}
                caption={caption}
                requestId={requestId || ''}
                controlId={''}
                linkageType={EVIDENCE_LINKAGE_TYPE.REQUEST_LINK}
                deleteFunc={deleteFunc}
              />
            ) : (
              <EvidenceDeleteButton
                deleteFunc={deleteFunc}
                disabled={!canDeleteEvidence}
              />
            )}
          </>
        )}
      </StyledActionEvidenceColumn>
    </>
  )
}

export type EvidenceIndexGridHeaderProps = {
  isReadonly?: boolean
  showContentMatchColumn?: boolean
}

export const EvidenceIndexGridHeader = ({
  isReadonly = false,
  showContentMatchColumn,
}: EvidenceIndexGridHeaderProps): JSX.Element => {
  const { searchParams, updateUrlParams } = useUrlSearchParams()
  const headerConfig = useEvidenceIndexHeaders(isReadonly)
  const sortBy =
    (searchParams.sort_by && (searchParams.sort_by[0] as GridColumnSortType)) ||
    GridColumnSortType.DESCENDING
  const sortCol: GridColumnSortEvidence | undefined =
    (searchParams.sort_col &&
      (searchParams.sort_col[0] as GridColumnSortEvidence)) ||
    GridColumnSortEvidence.RELEVANT_DATE

  const [columnMapState, dispatch] = useReducer(
    gridColSortReducerEvidence,
    GRID_COL_SORT_CONFIGS_EVIDENCE(sortBy, sortCol),
  )

  const fetchSortedEvidence = (action: GridColumnSortActionEvidence) => {
    dispatch(action)
    updateUrlParams({ sort_by: [action.type], sort_col: [action.colName] })
  }

  return (
    <GridRow>
      {showContentMatchColumn && <GridHeader />}
      {Object.values(EvidenceIndexHeader).map(
        (header: EvidenceIndexHeader, idx: number) => {
          if (!headerConfig[header]) {
            return null
          }
          const { name, gridAlign } =
            EVIDENCE_INDEX_HEADER_TO_HEADER_VALUE[header]
          return (
            <EvidenceGridColumns
              key={`${name}-${idx}`}
              gridAlign={gridAlign}
              adjustForNavbar={!isReadonly}
            >
              {name}
              {EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header] && (
                <EvidenceGridColumnSort
                  type={
                    columnMapState[
                      EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header]
                    ].type
                  }
                  isActive={
                    columnMapState[
                      EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header]
                    ].isActive
                  }
                  colName={EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header]}
                  onSort={fetchSortedEvidence}
                />
              )}
            </EvidenceGridColumns>
          )
        },
      )}
    </GridRow>
  )
}

type EvidenceGridColumnsProps = React.PropsWithChildren<
  Omit<
    EvidenceIndexGridHeaderProps,
    'selectable' | 'showContentMatchColumn'
  > & {
    key: string
    gridAlign: FlexAlign
    adjustForNavbar?: boolean
  }
>

const EvidenceGridColumns = ({
  children,
  adjustForNavbar = false,
  key,
  gridAlign,
}: EvidenceGridColumnsProps) => {
  const { bannerCount } = useBannerContext()
  if (adjustForNavbar) {
    return (
      <StickyGridHeader
        key={key}
        $gridAlign={gridAlign}
        $top={
          headerContentHeight + filterBarHeight + bannerHeight * bannerCount
        }
      >
        {children}
      </StickyGridHeader>
    )
  }
  return (
    <StickyGridHeader key={key} $gridAlign={gridAlign}>
      {children}
    </StickyGridHeader>
  )
}

export type EvidenceWrapperRowItemElementProps = {
  evidenceWrapper: EvidenceWrapper
  controlCount?: number
  showControlCount?: boolean
  isLinkBroken?: boolean
  requestId?: string
  linkedRequestsCount?: number
  onUpdate: () => Promise<void>
  newModel: boolean
  isControlShowPage?: boolean
}

export const evidenceWrapperAsRowItemElement = ({
  evidenceWrapper,
  controlCount,
  showControlCount,
  isLinkBroken,
  requestId,
  linkedRequestsCount,
  onUpdate,
  newModel,
  isControlShowPage,
}: EvidenceWrapperRowItemElementProps): RowItemElement => ({
  RowItem: (p: RowItemProp) => (
    <EvidenceWrapperGridRow
      evidenceWrapper={evidenceWrapper}
      gridRow={p.gridRow}
      total={controlCount}
      showControlCount={showControlCount}
      isLinkBroken={isLinkBroken}
      requestId={requestId}
      linkedRequestsCount={linkedRequestsCount}
      onUpdate={onUpdate}
      isControlShowPage={isControlShowPage}
    />
  ),
  key: evidenceWrapper.getId(),
  isDisabled:
    isLinkBroken || (newModel && !evidenceWrapper.getEvidenceInstanceId()),
})

type EvidenceWrapperRowItemsProps = {
  evidenceWrappers: EvidenceWrapper[]
  requestId?: string
  onUpdate: () => Promise<void>
  newModel: boolean
}

export const evidenceWrapperRowItems = ({
  evidenceWrappers,
  requestId,
  onUpdate,
  newModel,
}: EvidenceWrapperRowItemsProps): RowItemExpandableElement[] => {
  return evidenceWrappers.map((item) => {
    const evidence = item as EvidenceWrapper

    return {
      ...evidenceWrapperAsRowItemElement({
        evidenceWrapper: evidence,
        controlCount: evidence.getStackedEvidenceCount(),
        showControlCount: true,
        isLinkBroken: evidence.getIsLinkBroken(),
        requestId,
        linkedRequestsCount: evidence.getLinkedRequestsCount(),
        onUpdate,
        newModel,
      }),
      total: evidence.getStackedEvidenceCount(),
      expandedItems: {
        RowItem: ({
          gridRow,
          isExpanded,
        }: {
          gridRow: number
          isExpanded?: boolean
        }) => (
          <ExpandedEvidenceWrapperGrid
            evidenceID={evidence.getEvidenceId()}
            gridRow={gridRow}
            showControlCount={true}
            isExpanded={isExpanded}
            total={evidence.getStackedEvidenceCount()}
            requestId={requestId}
            linkedRequestsCount={evidence.getLinkedRequestsCount()}
            onUpdate={onUpdate}
          />
        ),
      },
      actionsRow: <></>,
      isDisabled:
        evidence.getIsLinkBroken() ||
        (newModel && !evidence.getEvidenceInstanceId()),
    } as RowItemExpandableElement
  })
}

export const ExpandedEvidenceWrapperGrid = ({
  evidenceID,
  gridRow,
  showControlCount,
  isExpanded,
  total,
  requestId,
  linkedRequestsCount,
  controlId,
  onUpdate: parentOnUpdate,
}: {
  evidenceID: string
  gridRow: number
  showControlCount?: boolean
  isExpanded?: boolean
  total?: number
  requestId?: string
  linkedRequestsCount?: number
  controlId?: string
  onUpdate?: () => Promise<void>
}): JSX.Element => {
  const newModel = useNewModel()
  const { data, isLoading, mutate } = useEvidenceInstanceList(
    evidenceID,
    controlId,
    isExpanded,
  )
  const ellipsis = useTypewriter({
    text: '...',
    interval: 500,
    shouldLoop: true,
  })

  const onUpdate = useCallback(async () => {
    await mutate()
    await parentOnUpdate?.()
  }, [mutate, parentOnUpdate])

  return isLoading ? (
    <>Loading {ellipsis}</>
  ) : (
    <>
      {data
        ?.getItemsList()
        .slice(1)
        .map((elem: EvidenceWrapper, idx: number) => (
          <GridRow key={`${idx}`}>
            {evidenceWrapperAsRowItemElement({
              evidenceWrapper: elem,
              controlCount: total,
              showControlCount,
              requestId,
              linkedRequestsCount: linkedRequestsCount,
              onUpdate,
              newModel,
              isControlShowPage: !!controlId,
            }).RowItem({
              gridRow: gridRow ? gridRow + idx : undefined,
            })}
          </GridRow>
        ))}
    </>
  )
}
